import React from 'react';
import { Modal } from '@opendoor/bricks/complex';
import { Box, Flex, Heading, Image, Text, Divider } from '@opendoor/bricks/core';
import { useObservability } from 'shared/helpers/observability';

import { useOfferContext } from 'sellDirect/OfferContext';
import { useSSEAContext } from 'sellDirect/SSEAContext';
import { DatePickerWrapper } from 'sellDirect/components/legacy/sellerDash/exteriorAssessment/DatePickerWrapper';
import { LocalDate } from '@js-joda/core';
import { UNSAFE_DatePicker } from '@opendoor/bricks/deprecated/UNSAFE_DatePicker';

const EXTERIOR_ASSESSMENT_MODAL_LABEL = 'exterior-assessment-modal';
const SCHEDULING_MODAL_HEADER_TEXT = 'Opendoor';
const SCHEDULING_MODAL_TITLE_TEXT = 'Exterior assessment';
const SCHEDULING_MODAL_CONTENT_TEXT =
  'An Opendoor Expert will visit your home and review the exterior. This typically takes around 30 minutes';
const AGENT_SCHEDULING_MODAL_CONTENT_TEXT =
  "An Opendoor Expert will visit your client's home and review the exterior. This typically takes around 30 minutes";

/** TODO: Add documentation */
export const SchedulingModal: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialDate?: React.ComponentProps<typeof UNSAFE_DatePicker>['initialDate'];
}> = ({ initialDate, isOpen, setIsOpen }) => {
  const { trackEvent } = useObservability();

  const { availableExteriorAssessmentSlots, setSelectedDate } = useSSEAContext();
  const { shouldSeeAgentExperience } = useOfferContext();

  const scheduleAppointment = (date: LocalDate) => {
    setSelectedDate(date);
    trackEvent('schedule-a-call', 'home-reserve-intro');
    setIsOpen(false);
  };

  const onClose = () => {
    trackEvent('schedule-a-call', 'home-reserve-intro-cancel');
    setIsOpen(false);
  };

  return (
    <Modal
      analyticsName="cake-legacy-ssea-scheduling-modal"
      isOpen={isOpen}
      shouldCloseOnOverlayClick
      showClose
      onRequestClose={onClose}
      aria-labelledby={EXTERIOR_ASSESSMENT_MODAL_LABEL}
    >
      <Flex flexDirection="column" justifyContent="center" alignItems="center">
        <Flex>
          <Box width="300px" mt={5} mb={8} textAlign="center">
            <DateHeader
              shouldSeeAgentExperience={shouldSeeAgentExperience}
              titleId={EXTERIOR_ASSESSMENT_MODAL_LABEL}
            />
          </Box>
        </Flex>
        <Divider />
        <DatePickerWrapper
          onClick={(date) => scheduleAppointment(date)}
          options={availableExteriorAssessmentSlots}
          title="Select a day"
          subtitle="Plan for 30 mins"
          initialDate={initialDate}
        />
      </Flex>
    </Modal>
  );
};

const DateHeader: React.FC<{ shouldSeeAgentExperience?: boolean; titleId: string }> = ({
  shouldSeeAgentExperience,
  titleId,
}) => {
  const schedulingModelContentText = shouldSeeAgentExperience
    ? AGENT_SCHEDULING_MODAL_CONTENT_TEXT
    : SCHEDULING_MODAL_CONTENT_TEXT;

  return (
    <>
      <Image
        borderRadius="rounded"
        src="https://imgdrop.imgix.net/59526cdabf3c46898d74ff6f4ae31752.svg"
        alt="Opendoor logo"
      />
      <Text fontSize="s0" lineHeight="s00">
        {SCHEDULING_MODAL_HEADER_TEXT}
      </Text>
      <Heading id={titleId} size="h3" mb={3}>
        {SCHEDULING_MODAL_TITLE_TEXT}
      </Heading>
      <Text fontSize="s0" lineHeight="s00" textAlign="left">
        {schedulingModelContentText}
      </Text>
    </>
  );
};
